import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import contractorContactsService from '../services/contractorContactsService';
import ticketsService from '../services/ticketsService';
import styles from '../assets/contractorContactCalendar.module.css';
import Loader from '../components/Loader';

// FullCalendar imports - Laravel exact setup
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';

// PDF export - Simple browser-based approach

const ContractorContactCalendar = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasPermission: authHasPermission, isContractor } = useAuth();
  const calendarRef = useRef(null);
  const dataTableRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [allTickets, setAllTickets] = useState(0);
  const [statuses, setStatuses] = useState([]);
  const [contactsForCalendar, setContactsForCalendar] = useState([]);
  const [setUpTickets, setSetUpTickets] = useState(0);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [clickedStatusId, setClickedStatusId] = useState(null);

  // Editable modal fields - Laravel exact match
  const [modalBookInDay, setModalBookInDay] = useState('');
  const [modalBookInTimeFrom, setModalBookInTimeFrom] = useState('');
  const [modalBookInTimeTo, setModalBookInTimeTo] = useState('');
  const [modalErrors, setModalErrors] = useState({});
  const [modalSaving, setModalSaving] = useState(false);

  // DataTable states - matching Laravel DataTables exactly
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [entriesPerPage, setEntriesPerPage] = useState(30);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('ticketId');
  const [sortDirection, setSortDirection] = useState('desc');

  // Column visibility state - Laravel exact match with columns list
  const [showColumnVisibility, setShowColumnVisibility] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState({
    ticketId: true,
    status: true,
    urgency: true,
    property_id: false, // Hidden by default as per Laravel
    property_name: true,
    postcode: true,
    created_at: true,
    book_in_date: true,
    date_estimated_completion: true,
    date_completed: true,
    job_type: true,
    actions: true
  });

  useEffect(() => {
    loadContactCalendarData();
  }, [id]);

  // Close column visibility dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      const dtButtonGroup = event.target.closest(`.${styles['dt-button-group']}`);
      if (!dtButtonGroup) {
        setShowColumnVisibility(false);
      }
    };

    if (showColumnVisibility) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColumnVisibility]);

  useEffect(() => {
    if (contact && tickets.length > 0) {
      initializeCalendar();
      initializeDataTable();
    }
  }, [contact, tickets, clickedStatusId]);

  // Debug modal state changes
  useEffect(() => {
    console.log('🔍 Modal state changed:', { showTicketModal, selectedTicket: selectedTicket?.id });
  }, [showTicketModal, selectedTicket]);

  // Debounce search input - wait for user to stop typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
      setCurrentPage(1); // Reset to page 1 when search changes
    }, 400); // 400ms debounce delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadContactCalendarData = async () => {
    try {
      setLoading(true);
      console.log(`📅 Loading calendar data for contact ${id}...`);

      // Call real API endpoint
      const response = await contractorContactsService.getCalendarData(id);
      console.log('📅 API Response:', response);
      console.log('📅 Contact from API:', response.contact);
      console.log('📅 Contact full name:', response.contact?.user?.full_name);

      if (!response.success) {
        throw new Error(response.message || 'Failed to load calendar data');
      }

      // Transform API response to match component state structure
      const apiContact = response.contact;
      const apiTickets = response.tickets || [];

      // ✅ FIX #3: Add isOverdue field to tickets (HIGH PRIORITY)
      const dataTableTickets = apiTickets.map(t => {
        // ✅ Calculate isOverdue based on deadline and status "IN ACTION"
        // Laravel logic: Red background only for overdue tickets that are IN ACTION
        // Parse DD/MM/YYYY format from backend
        let isOverdue = false;
        const statusNameLower = (t.status || '').toLowerCase();

        if (t.date_estimated_completion &&
            t.date_estimated_completion !== 'Not specified' &&
            !t.date_completed &&
            statusNameLower === 'in action') { // Only IN ACTION status gets red background

          // Parse date from DD/MM/YYYY format
          const parts = t.date_estimated_completion.split('/');
          if (parts.length === 3) {
            const deadline = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`); // YYYY-MM-DD
            const today = new Date();
            today.setHours(0, 0, 0, 0); // Reset to start of day for accurate comparison
            isOverdue = deadline < today;

            // Debug log for first ticket
            if (t.id === 90039) {
              console.log(`🔴 Ticket #${t.id} overdue check:`, {
                deadline_str: t.date_estimated_completion,
                deadline_parsed: deadline,
                today: today,
                isOverdue: isOverdue,
                completed: t.date_completed,
                status_id: t.status_id
              });
            }
          }
        }

        return {
          ticketId: t.id,
          id: t.id,
          status: t.status,
          statusColor: t.statusColor,
          urgency: t.urgency,
          property_id: t.property_property_id,
          property_name: t.property_name,
          postcode: t.postcode,
          created_at: t.created_at,
          book_in_date: t.book_in_date_formatted,
          book_in_time_from: t.book_in_time_from,
          book_in_time_to: t.book_in_time_to,
          date_estimated_completion: t.date_estimated_completion,
          date_completed: t.date_completed,
          job_type: t.job_type,
          status_id: parseInt(t.status_id) || t.status_id,
          // Certificate renewal tickets are completed by submitting the certificate,
          // not via the Complete button.
          is_certificate_renewal: !!Number(t.is_certificate_renewal),
          isOverdue: isOverdue, // ✅ FIX #3: Add overdue flag
          // For calendar and modal
          title: `#${t.id} - ${t.property_name || 'No Property'}`,
          ticket_status: t.status,
          contractor_user: t.contractor_user,
          contractor_decision: t.contractor_decision,
          calendar_description: t.calendar_description || t.description,
          // Calendar event fields - use ISO date format from backend
          // Format: YYYY-MM-DDTHH:MM:00 (FullCalendar expects seconds)
          start: t.book_in_date_iso && t.book_in_time_from ?
            `${t.book_in_date_iso}T${t.book_in_time_from}:00` : null,
          end: t.book_in_date_iso && t.book_in_time_to ?
            `${t.book_in_date_iso}T${t.book_in_time_to}:00` : null,
          color: t.statusColor || '#3788d8',
          textColor: '#fff'
        };
      });

      console.log(`📅 Mapped ${dataTableTickets.length} tickets for DataTable`);

      // Set all state from API response
      setContact(apiContact);
      setTickets(dataTableTickets); // Store transformed tickets for both DataTable AND Calendar
      setAllTickets(response.allTickets);
      setStatuses(response.statuses || []);
      setContactsForCalendar(response.contactsForCalendar || []);
      setSetUpTickets(response.setUpTickets || 0);

      console.log(`✅ Calendar loaded: ${response.allTickets} total tickets, ${response.statuses?.length || 0} statuses`);

    } catch (err) {
      console.error('❌ Error loading contractor contact calendar:', err);
      toast.error(err.message || 'Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  };

  // Convert tickets to calendar events - Laravel exact format
  const getCalendarEvents = () => {
    // Only include tickets that have valid start/end times for calendar display
    return getFilteredTickets()
      .filter(ticket => ticket.start && ticket.end) // Filter tickets with valid dates
      .map(ticket => ({
        id: ticket.id,
        title: ticket.title,
        start: ticket.start,
        end: ticket.end,
        color: ticket.color,
        textColor: ticket.textColor,
        // Laravel-specific event data
        ticket_status: ticket.ticket_status,
        calendar_description: ticket.calendar_description,
        contractor_user: ticket.contractor_user,
        contractor_decision: ticket.contractor_decision,
        // Add additional properties for modal
        ticketId: ticket.ticketId,
        property_name: ticket.property_name,
        book_in_time_from: ticket.book_in_time_from,
        book_in_time_to: ticket.book_in_time_to,
        status_id: ticket.status_id
      }));
  };

  const initializeCalendar = () => {
    // Calendar is now handled by FullCalendar React component
    console.log('Calendar initialized with', getCalendarEvents().length, 'events');
  };

  const initializeDataTable = () => {
    if (!dataTableRef.current) return;
    // DataTable is now handled by React state
    console.log('DataTable initialized');
  };

  // Calendar event click handler - Laravel exact functionality
  const handleCalendarEventClick = (info) => {
    console.log('🎯 Calendar event clicked:', info.event);
    const event = info.event;

    // Find the full ticket data from tickets array
    const fullTicket = tickets.find(t => t.id === parseInt(event.id));
    console.log('📋 Full ticket found:', fullTicket);

    const ticketData = {
      id: event.id,
      title: event.title,
      ticket_status: event.extendedProps.ticket_status,
      contractor_user: event.extendedProps.contractor_user,
      contractor_decision: event.extendedProps.contractor_decision,
      calendar_description: event.extendedProps.calendar_description,
      ticketId: event.extendedProps.ticketId,
      property_name: event.extendedProps.property_name,
      book_in_time_from: event.extendedProps.book_in_time_from,
      book_in_time_to: event.extendedProps.book_in_time_to,
      status_id: event.extendedProps.status_id,
      // Certificate renewal tickets are completed by submitting the certificate, not
      // via the modal Complete button.
      is_certificate_renewal: !!fullTicket?.is_certificate_renewal,
      // Add book_in_date for modal editing
      book_in_date: fullTicket?.book_in_date || ''
    };

    console.log('📝 Ticket data prepared:', ticketData);

    setSelectedTicket(ticketData);

    // Pre-populate modal fields with existing values
    setModalBookInDay(fullTicket?.book_in_date || '');
    setModalBookInTimeFrom(event.extendedProps.book_in_time_from || '');
    setModalBookInTimeTo(event.extendedProps.book_in_time_to || '');
    setModalErrors({});

    console.log('✅ Setting showTicketModal to true');
    setShowTicketModal(true);
    
    // Prevent default event propagation
    info.jsEvent?.preventDefault();
    info.jsEvent?.stopPropagation();
  };

  // Laravel activity box click handler - exact match
  const handleStatusBoxClick = (statusId) => {
    setClickedStatusId(statusId);
    setCurrentPage(1); // Reset to first page
    console.log('Filter by status:', statusId);
  };

  const handleTicketClick = (ticket) => {
    setSelectedTicket(ticket);

    // Pre-populate modal fields with existing values
    setModalBookInDay(ticket.book_in_date || '');
    setModalBookInTimeFrom(ticket.book_in_time_from || '');
    setModalBookInTimeTo(ticket.book_in_time_to || '');
    setModalErrors({});

    setShowTicketModal(true);
  };

  // Update ticket from contractor calendar modal - Laravel exact match
  const handleUpdateTicketFromModal = async () => {
    try {
      setModalSaving(true);
      setModalErrors({});

      console.log('📋 Updating ticket from contractor calendar modal...');

      // Validate fields - Laravel format d/m/Y and H:i
      const errors = {};

      // Date format validation: dd/mm/yyyy
      if (modalBookInDay && !/^\d{2}\/\d{2}\/\d{4}$/.test(modalBookInDay)) {
        errors.book_in_day = 'Date must be in format DD/MM/YYYY';
      }

      // Time format validation: HH:mm
      if (modalBookInTimeFrom && !/^\d{2}:\d{2}$/.test(modalBookInTimeFrom)) {
        errors.book_in_time_from = 'Time must be in format HH:MM';
      }

      if (modalBookInTimeTo && !/^\d{2}:\d{2}$/.test(modalBookInTimeTo)) {
        errors.book_in_time_to = 'Time must be in format HH:MM';
      }

      if (Object.keys(errors).length > 0) {
        setModalErrors(errors);
        setModalSaving(false);
        return;
      }

      // Call API endpoint
      const response = await ticketsService.updateFromContractorCalendar(
        selectedTicket.ticketId,
        {
          book_in_day: modalBookInDay,
          book_in_time_from: modalBookInTimeFrom,
          book_in_time_to: modalBookInTimeTo
        }
      );

      if (response.status === 'success') {
        toast.success('Ticket updated successfully');
        setShowTicketModal(false);

        // Reload calendar data
        await loadContactCalendarData();
      } else {
        toast.error(response.message || 'Failed to update ticket');
      }

    } catch (error) {
      console.error('❌ Error updating ticket from modal:', error);

      // Handle Laravel validation errors
      if (error.response?.data?.errors) {
        setModalErrors(error.response.data.errors);
      } else {
        toast.error(error.message || 'Failed to update ticket');
      }
    } finally {
      setModalSaving(false);
    }
  };

  const handleShowTicket = (ticketId) => {
    // Navigate to ticket show page - matches Laravel behavior
    window.location.href = `/tickets/${ticketId}`;
  };

  const handleEditTicket = (ticketId) => {
    // Navigate to ticket edit page - matches Laravel behavior
    window.location.href = `/tickets/${ticketId}/edit`;
  };

  const handleContactChange = (contactId) => {
    if (contactId && contactId !== '') {
      navigate(`/contractors/contacts/${contactId}/calendar`);
    }
  };

  const handleTicketAction = async (ticketId, action) => {
    // Different confirmation messages based on action
    const confirmMessages = {
      accept: "Are you sure you want to accept the ticket?",
      decline: "Are you sure you want to decline the ticket?",
      complete: "Are you sure?",
      delete: "Are you sure you want to remove?"
    };

    if (!window.confirm(confirmMessages[action] || `Are you sure you want to ${action} this ticket?`)) {
      return;
    }

    try {
      console.log(`🎬 ${action} ticket #${ticketId}...`);

      // Call real API endpoints
      if (action === 'accept') {
        await ticketsService.acceptTicketByContractor(ticketId);
        toast.success('Ticket accepted successfully');
      } else if (action === 'decline') {
        // For contractor calendar, decline without reason
        await ticketsService.declineTicketByContractor(ticketId, 'Declined from calendar');
        toast.success('Ticket declined successfully');
      } else if (action === 'complete') {
        // For contractor calendar, complete without cost of works
        await ticketsService.completeTicketByContractor(ticketId, null, 'Completed from calendar');
        toast.success('Ticket completed successfully');
      } else if (action === 'delete') {
        await ticketsService.deleteTicket(ticketId);
        toast.success('Ticket removed successfully');
      }

      console.log(`✅ Ticket ${action}ed successfully`);

      // Close modal and reload data
      if (action === 'accept' || action === 'decline' || action === 'complete' || action === 'delete') {
        setShowTicketModal(false);
      }
      await loadContactCalendarData();

    } catch (error) {
      console.error(`❌ Error ${action} ticket:`, error);
      toast.error(error.message || `Failed to ${action} ticket`);
    }
  };

  // Column visibility functions - Laravel exact colVis functionality
  const toggleColumnVisibility = (columnKey) => {
    console.log('Toggling column visibility for:', columnKey);
    setColumnVisibility(prev => {
      const newVisibility = {
        ...prev,
        [columnKey]: !prev[columnKey]
      };
      console.log('New column visibility:', newVisibility);
      return newVisibility;
    });
  };

  const handleColumnVisibilityToggle = () => {
    setShowColumnVisibility(!showColumnVisibility);
  };

  // ✅ FIX #1: Real permission system from AuthContext (CRITICAL)
  const hasPermission = (permission) => {
    return authHasPermission(permission);
  };

  // Render action buttons based on ticket status - Laravel exact logic
  const renderActionButtons = (ticket) => {
    const statusId = ticket.status_id;
    const REQUEST_STATUS = 1; // config('tickets.statuses_reverse.Request')
    const COMPLETED_STATUS = 5; // config('tickets.statuses_reverse.Completed')
    const CONTRACTOR_COMPLETED_STATUS = 7; // config('tickets.statuses_reverse.Contractor Completed')

    return (
      <div className={`${styles.actionButtons}`}>
        {/* Show button - always visible if permission */}
        {hasPermission('tickets.show') && (
          <button
            className={`${styles.btn} ${styles.btnSm} ${styles.btnWarning}`}
            onClick={() => handleShowTicket(ticket.ticketId)}
          >
            Show
          </button>
        )}

        {/* Edit button */}
        {hasPermission('tickets.edit') && (
          <button
            className={`${styles.btn} ${styles.btnSm} ${styles.btnPrimary}`}
            onClick={() => handleEditTicket(ticket.ticketId)}
          >
            Edit
          </button>
        )}

        {/* Delete button - hidden for contractors (only staff can delete) */}
        {hasPermission('tickets.delete') && !isContractor() && (
          <button
            className={`${styles.btn} ${styles.btnSm} ${styles.btnDanger}`}
            onClick={() => handleTicketAction(ticket.ticketId, 'delete')}
          >
            Delete
          </button>
        )}

        {/* Contractor management buttons - Laravel exact logic */}
        {hasPermission('tickets.contractors.manage') && statusId && (
          <>
            {statusId === REQUEST_STATUS && (
              <>
                <button
                  className={`${styles.btn} ${styles.btnSm} ${styles.btnSuccess}`}
                  onClick={() => handleTicketAction(ticket.ticketId, 'accept')}
                >
                  Accept
                </button>
                {/* Decline button hidden for contractors - only staff can decline */}
                {hasPermission('tickets.delete') && !isContractor() && (
                  <button
                    className={`${styles.btn} ${styles.btnSm} ${styles.btnDanger}`}
                    onClick={() => handleTicketAction(ticket.ticketId, 'decline')}
                  >
                    Decline
                  </button>
                )}
              </>
            )}
            {/* Laravel exact match: Complete button shows for all statuses except COMPLETED (status_id 5) */}
            {/* Note: CONTRACTOR_COMPLETED (status_id 7) should still show Complete button */}
            {/* Certificate renewal tickets are excluded: they must be completed by uploading
                the certificate on the ticket ("Submit for review"), never by Complete alone. */}
            {statusId !== COMPLETED_STATUS && !ticket.is_certificate_renewal && (
              <button
                className={`${styles.btn} ${styles.btnSm} ${styles.btnInfo}`}
                onClick={() => handleTicketAction(ticket.ticketId, 'complete')}
              >
                Complete
              </button>
            )}
          </>
        )}
      </div>
    );
  };

  // DataTable functions - Laravel exact functionality
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const getSortedTickets = () => {
    let sorted = [...tickets];

    if (sortField) {
      sorted.sort((a, b) => {
        let aValue = a[sortField] || '';
        let bValue = b[sortField] || '';

        if (sortField.includes('date') || sortField.includes('created_at')) {
          aValue = new Date(aValue || 0);
          bValue = new Date(bValue || 0);
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        }

        const comparison = aValue.toString().localeCompare(bValue.toString());
        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }

    return sorted;
  };

  const getFilteredTickets = () => {
    let filtered = getSortedTickets();

    // Status filter - Laravel exact logic
    if (clickedStatusId && clickedStatusId !== 'building_set_up_service') {
      filtered = filtered.filter(ticket => ticket.status_id === clickedStatusId);
    } else if (clickedStatusId === 'building_set_up_service') {
      // Special filter for SET UP tickets
      filtered = filtered.filter(ticket => ticket.job_type === 'SET UP' || ticket.job_type === 'Building Setup');
    }

    // Search filter - only search visible columns (uses debounced + trimmed search term)
    if (debouncedSearchTerm) {
      const term = debouncedSearchTerm.toLowerCase();
      filtered = filtered.filter(ticket => {
        // Only search columns that are visible
        if (columnVisibility.ticketId && ticket.ticketId?.toString().includes(debouncedSearchTerm)) return true;
        if (columnVisibility.status && ticket.status?.toLowerCase().includes(term)) return true;
        if (columnVisibility.urgency && ticket.urgency?.toLowerCase().includes(term)) return true;
        if (columnVisibility.property_id && ticket.property_id?.toLowerCase().includes(term)) return true;
        if (columnVisibility.property_name && ticket.property_name?.toLowerCase().includes(term)) return true;
        if (columnVisibility.postcode && ticket.postcode?.toLowerCase().includes(term)) return true;
        if (columnVisibility.job_type && ticket.job_type?.toLowerCase().includes(term)) return true;
        if (columnVisibility.created_at && ticket.created_at?.toLowerCase().includes(term)) return true;
        if (columnVisibility.book_in_date && ticket.book_in_date?.toLowerCase().includes(term)) return true;
        if (columnVisibility.date_estimated_completion && ticket.date_estimated_completion?.toLowerCase().includes(term)) return true;
        if (columnVisibility.date_completed && ticket.date_completed?.toLowerCase().includes(term)) return true;
        return false;
      });
    }

    return filtered;
  };

  const getPaginatedTickets = () => {
    const filtered = getFilteredTickets();
    if (entriesPerPage === -1) {
      return filtered; // Show all
    }
    const startIndex = (currentPage - 1) * entriesPerPage;
    const endIndex = startIndex + entriesPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  const totalEntries = getFilteredTickets().length;
  const totalPages = entriesPerPage === -1 ? 1 : Math.ceil(totalEntries / entriesPerPage);
  const startRecord = totalEntries > 0 ? (currentPage - 1) * (entriesPerPage === -1 ? 0 : entriesPerPage) + 1 : 0;
  const endRecord = entriesPerPage === -1 ? totalEntries : Math.min(currentPage * entriesPerPage, totalEntries);

  // Export functions - Laravel DataTables exact functionality
  const handleExportCSV = () => {
    const headers = ['ID', 'Status', 'Urgency', 'Property ID', 'Property Name', 'Postcode', 'Submitted', 'Book in', 'Deadline', 'Completed', 'Job type'];
    const csvRows = [headers.join(',')];

    getFilteredTickets().forEach(ticket => {
      const row = [
        ticket.ticketId,
        ticket.status,
        ticket.urgency,
        ticket.property_id || '',
        ticket.property_name || '',
        ticket.postcode || '',
        ticket.created_at || '',
        ticket.book_in_date || '',
        ticket.date_estimated_completion || '',
        ticket.date_completed || '',
        ticket.job_type || ''
      ];
      csvRows.push(row.join(','));
    });

    const csv = csvRows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contractor-contact-tickets.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('CSV exported successfully');
  };

  const handleExportPDF = async () => {
    try {
      // Validate we have data
      const filteredTickets = getFilteredTickets();
      if (!filteredTickets || filteredTickets.length === 0) {
        toast.warning('No tickets to export');
        return;
      }

      toast.info('Generating PDF...');

      // Call backend API to generate PDF
      const token = localStorage.getItem('token');
      const ticketsJSON = encodeURIComponent(JSON.stringify(filteredTickets));
      const url = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001'}/api/export/contractor-tickets/pdf?contactId=${id}&tickets=${ticketsJSON}`;

      // Download PDF using fetch
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }

      // Get blob and download
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `contractor-tickets-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF');
    }
  };

  const handlePrint = () => {
    window.print();
    toast.success('Print dialog opened');
  };

  if (loading) {
    return <Loader />;
  }

  if (!contact) {
    return (
      <div className={`${styles.containerFluid}`}>
        <div className={`${styles.row}`}>
          <div className={`${styles['col-md-12']}`}>
            <div className="alert alert-danger">
              Contact not found
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.containerFluid}`}>

      {/* ✅ FIX #7: Breadcrumbs - Laravel exact match with advanced variant */}
      <div className={`${styles.row}`}>
        <div className={`${styles['col-md-12']}`}>
          <ol className={`${styles.breadcrumb}`}>
            <li>
              <a href="/dashboard">Dashboard</a>
            </li>
            {hasPermission('contractors.edit') && (
              <>
                <li>
                  <a href="/contractors">Contractors</a>
                </li>
                <li>
                  <a href={`/contractors/${contact.contractor?.id}`}>{contact.contractor?.name || 'Contractor'}</a>
                </li>
                <li>
                  <a href={`/contractors/contacts/${contact.id}`}>{contact.user?.full_name || 'Contact'}</a>
                </li>
              </>
            )}
            <li className={`${styles.active}`}>Calendar</li>
          </ol>
        </div>
      </div>

      <div className={`${styles.row} ${styles.jsTicketsOfContractorContact}`}>

        {/* Hidden form field for status filter - Laravel exact */}
        <input type="hidden" name="clickedStatusId" value={clickedStatusId || ''} />

        {/* Main Calendar Panel */}
        <div className={`${styles['col-md-12']}`}>
          <div className={`${styles.panel} ${styles.panelDefault} ${styles.panelStandard}`}>
            <div id="ticket-status-span" className={`${styles.panelHeading} ${styles.panelTitle}`}>
              <h3>{contact.user.full_name} <span className={`${styles.textMuted}`}>'s tickets</span></h3>
            </div>
            <div className={`${styles.panelBody}`}>
              <div
                id="tickets-calendar-contractor-contact"
                ref={calendarRef}
                className={`${styles.calendarContainer}`}
              >
                {/* FullCalendar - Laravel exact configuration (v5 view names) */}
                <FullCalendar
                  plugins={[dayGridPlugin, timeGridPlugin, listPlugin]}
                  headerToolbar={{
                    left: '',
                    center: 'prev title next',
                    right: 'listWeek,dayGridMonth,timeGridWeek,timeGridDay'
                  }}
                  initialView='timeGridDay'
                  timeFormat='HH:mm'
                  slotMinTime='06:00:00'
                  firstDay={1} // Monday
                  editable={false} // LARAVEL MATCH: NO drag-and-drop
                  droppable={false} // LARAVEL MATCH: NO drag-and-drop
                  height='auto'
                  contentHeight='400px'
                  events={getCalendarEvents()}
                  eventClick={handleCalendarEventClick}
                  eventDisplay='block'
                  dayMaxEvents={false}
                  moreLinkClick='popover'
                  // Custom event rendering - Laravel style
                  eventDidMount={(info) => {
                    // Add Laravel-style content to events
                    const event = info.event;
                    const element = info.el;

                    // Add status and description to event content
                    const contentEl = element.querySelector('.fc-event-title, .fc-list-event-title, .fc-event-main');
                    if (contentEl && event.extendedProps.calendar_description) {
                      contentEl.innerHTML = `
                        <div>${event.title}</div>
                        <div style="font-size: 11px; opacity: 0.8;">${event.extendedProps.calendar_description}</div>
                      `;
                    }

                    // For list view, show status in time column
                    const timeEl = element.querySelector('.fc-list-event-time');
                    if (timeEl && event.extendedProps.ticket_status) {
                      timeEl.innerHTML = event.extendedProps.ticket_status;
                    }
                  }}
                  // Add custom styling
                  eventClassNames={(arg) => {
                    return ['fc-event-custom'];
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Status Overview - Laravel exact structure */}
        <div className={`${styles['col-md-12']}`}>
          <div className={`${styles.panelTitle} ${styles.titleStandard}`}>
            <h3>Status overview</h3>
          </div>
          <div className={`${styles.panelGroup} ${styles.marginDown}`}>

            {statuses.filter(status => {
              const name = status.name?.toLowerCase() || '';
              return !name.includes('new') && !name.includes('awaiting');
            }).map(status => (
              <div key={status.id} className={`${styles['col-md-2']} ${styles['col-lg-2']}`}>
                <div
                  className={`${styles['activity-box']} ${styles['js-ticket-activity-box']}`}
                  style={{ backgroundColor: status.color }}
                  data-status_id={status.id}
                  onClick={() => handleStatusBoxClick(status.id)}
                >
                  <p className={`${styles['activity-value']}`}>{status.count}</p>
                  <p className={`${styles['activity-title']}`}>{status.name}</p>
                </div>
              </div>
            ))}

            <div className={`${styles['col-md-2']} ${styles['col-lg-2']}`}>
              <div
                className={`${styles['activity-box']} ${styles['js-ticket-activity-box']}`}
                style={{ backgroundColor: '#0a7280' }}
                onClick={() => handleStatusBoxClick(null)}
              >
                <p className={`${styles['activity-value']}`}>{allTickets}</p>
                <p className={`${styles['activity-title']}`}>ALL</p>
              </div>
            </div>

            {/* SET UP tickets for LR contractors - Laravel exact logic */}
            {contact.contractor?.isLR && (
              <div className={`${styles['col-md-2']} ${styles['col-lg-2']}`}>
                <div
                  className={`${styles['activity-box']} ${styles['js-ticket-activity-box']}`}
                  style={{ backgroundColor: 'midnightblue' }}
                  data-status_id="building_set_up_service"
                  onClick={() => handleStatusBoxClick('building_set_up_service')}
                >
                  <p className={`${styles['activity-value']}`}>{setUpTickets}</p>
                  <p className={`${styles['activity-title']}`}>SET UP</p>
                </div>
              </div>
            )}

            <div className={`${styles.clearfix}`}></div>
          </div>
        </div>

        {/* Go to contractor contact calendar dropdown - Laravel exact */}
        {hasPermission('contractors.edit') && (
          <div className={`${styles['col-md-12']} ${styles.mb20}`}>
            <div className={`${styles.propertyViewingsGoToUserCalendarGroup}`}>
              <span><strong>Go to contractor contact calendar: </strong></span>
              <select
                className={`${styles.formControl} ${styles['js-ticket-go-to-contractor-contact-calendar']}`}
                onChange={(e) => handleContactChange(e.target.value)}
                defaultValue=""
              >
                <option value="">Please select</option>
                {contactsForCalendar.map(contact => (
                  <option key={contact.id} value={contact.id}>
                    {contact.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* DataTable Panel - Laravel exact structure */}
        <div className={`${styles['col-md-12']}`}>
          <div className={`${styles.panel} ${styles.panelDefault} ${styles.panelStandard}`}>
            <div id="property-status-span" className={`${styles.panelHeading} ${styles.panelTitle}`}>
              <h3>List</h3>
            </div>
            <div className={`${styles.panelBody}`}>

              <input type="hidden" name="clickedStatusId" value={clickedStatusId || ''} />

              {tickets.length > 0 ? (
                <div className={`${styles.dataTableWrapper}`} style={{overflowX: 'auto'}}>

                  {/* DataTables toolbar - Laravel exact match */}
                  <div className={`${styles['dt-toolbar']}`}>
                    <div className={`${styles.dataTables_length}`}>
                      <label>
                        Show
                        <select
                          value={entriesPerPage}
                          onChange={(e) => {
                            setEntriesPerPage(Number(e.target.value));
                            setCurrentPage(1);
                          }}
                        >
                          <option value={30}>30</option>
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={75}>75</option>
                          <option value={100}>100</option>
                          <option value={-1}>All</option>
                        </select>
                         entries
                      </label>
                    </div>

                    <div className={`${styles['dt-buttons']}`}>
                      <button
                        className={`${styles['dt-button']}`}
                        onClick={handleExportCSV}
                        title="Export as CSV"
                      >
                        Export as CSV
                      </button>
                      <button
                        className={`${styles['dt-button']}`}
                        onClick={handleExportPDF}
                        title="Export as PDF"
                      >
                        Export as PDF
                      </button>
                      <button
                        className={`${styles['dt-button']}`}
                        onClick={handlePrint}
                        title="Print table"
                      >
                        Print table
                      </button>
                      <div className={`${styles['dt-button-group']}`}>
                        <button
                          className={`${styles['dt-button']} ${styles['buttons-colvis']}`}
                          onClick={handleColumnVisibilityToggle}
                          title="Column visibility"
                        >
                          Columns visibility
                        </button>
                        {showColumnVisibility && (
                          <div className={`${styles['dt-button-collection']}`}>
                            {Object.entries(columnVisibility).map(([key, visible]) => {
                              const columnLabels = {
                                ticketId: 'ID',
                                status: 'Status',
                                urgency: 'Urgency',
                                property_id: 'Property ID',
                                property_name: 'Property Name',
                                postcode: 'Postcode',
                                created_at: 'Submitted',
                                book_in_date: 'Book in',
                                date_estimated_completion: 'Deadline',
                                date_completed: 'Completed',
                                job_type: 'Job type',
                                actions: 'Actions'
                              };

                              return (
                                <button
                                  key={key}
                                  className={`${styles['dt-button']} ${styles['buttons-columnVisibility']} ${visible ? styles.active : ''}`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    toggleColumnVisibility(key);
                                  }}
                                >
                                  {columnLabels[key]}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className={`${styles.dataTables_filter}`}>
                      <label>
                        Search:
                        <input
                          type="text"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value.trimStart())}
                        />
                      </label>
                    </div>
                  </div>

                  <table
                    id="datatable-tickets-of-contractor-contact"
                    ref={dataTableRef}
                    data-contact={contact.id}
                    className={`${styles.table} ${styles['table-hover']} ${styles['dt-responsive']} ${styles['js-tickets-dt']}`}
                  >
                    <thead>
                      <tr style={{ fontSize: '11px' }}>
                        {columnVisibility.ticketId && (
                          <th onClick={() => handleSort('ticketId')} style={{cursor: 'pointer'}}>
                            ID {sortField === 'ticketId' && <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                          </th>
                        )}
                        {columnVisibility.status && (
                          <th onClick={() => handleSort('status')} style={{cursor: 'pointer'}}>
                            Status {sortField === 'status' && <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                          </th>
                        )}
                        {columnVisibility.urgency && (
                          <th onClick={() => handleSort('urgency')} style={{cursor: 'pointer'}}>
                            Urgency {sortField === 'urgency' && <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                          </th>
                        )}
                        {columnVisibility.property_id && (
                          <th onClick={() => handleSort('property_id')} style={{cursor: 'pointer'}}>
                            Property ID {sortField === 'property_id' && <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                          </th>
                        )}
                        {columnVisibility.property_name && (
                          <th onClick={() => handleSort('property_name')} style={{cursor: 'pointer'}}>
                            Property Name {sortField === 'property_name' && <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                          </th>
                        )}
                        {columnVisibility.postcode && (
                          <th onClick={() => handleSort('postcode')} style={{cursor: 'pointer'}}>
                            Postcode {sortField === 'postcode' && <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                          </th>
                        )}
                        {columnVisibility.created_at && (
                          <th
                            onClick={() => handleSort('created_at')}
                            style={{cursor: 'pointer'}}
                            className={`${styles['js-tooltip']}`}
                            title="Date when submitted"
                          >
                            Submitted {sortField === 'created_at' && <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                          </th>
                        )}
                        {columnVisibility.book_in_date && (
                          <th
                            onClick={() => handleSort('book_in_date')}
                            style={{cursor: 'pointer'}}
                            className={`${styles['js-tooltip']}`}
                            title="Book in date"
                          >
                            Book in {sortField === 'book_in_date' && <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                          </th>
                        )}
                        {columnVisibility.date_estimated_completion && (
                          <th
                            onClick={() => handleSort('date_estimated_completion')}
                            style={{cursor: 'pointer'}}
                            className={`${styles['js-tooltip']}`}
                            title="Proposed deadline"
                          >
                            Deadline {sortField === 'date_estimated_completion' && <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                          </th>
                        )}
                        {columnVisibility.date_completed && (
                          <th
                            onClick={() => handleSort('date_completed')}
                            style={{cursor: 'pointer'}}
                            className={`${styles['js-tooltip']}`}
                            title="Date when completed"
                          >
                            Completed {sortField === 'date_completed' && <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                          </th>
                        )}
                        {columnVisibility.job_type && (
                          <th onClick={() => handleSort('job_type')} style={{cursor: 'pointer'}}>
                            Job type {sortField === 'job_type' && <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                          </th>
                        )}
                        {columnVisibility.actions && (
                          <th>Actions</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {getPaginatedTickets().map(ticket => (
                        <tr
                          key={ticket.ticketId}
                          className={ticket.isOverdue ? styles.rowTicketDanger : ''}
                        >
                          {columnVisibility.ticketId && (
                            <td>{ticket.ticketId}</td>
                          )}
                          {columnVisibility.status && (
                            <td>
                              <span
                                className={`${styles.label}`}
                                style={{
                                  backgroundColor: ticket.statusColor,
                                  color: 'white',
                                  padding: '3px 8px',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  textTransform: 'uppercase'
                                }}
                              >
                                {ticket.status}
                              </span>
                            </td>
                          )}
                          {columnVisibility.urgency && (
                            <td>{ticket.urgency}</td>
                          )}
                          {columnVisibility.property_id && (
                            <td>{ticket.property_id}</td>
                          )}
                          {columnVisibility.property_name && (
                            <td>{ticket.property_name}</td>
                          )}
                          {columnVisibility.postcode && (
                            <td>{ticket.postcode}</td>
                          )}
                          {columnVisibility.created_at && (
                            <td>{ticket.created_at}</td>
                          )}
                          {columnVisibility.book_in_date && (
                            <td>{ticket.book_in_date}</td>
                          )}
                          {columnVisibility.date_estimated_completion && (
                            <td>{ticket.date_estimated_completion}</td>
                          )}
                          {columnVisibility.date_completed && (
                            <td>{ticket.date_completed}</td>
                          )}
                          {columnVisibility.job_type && (
                            <td>{ticket.job_type}</td>
                          )}
                          {columnVisibility.actions && (
                            <td className={`action ${styles.hiddenPrint}`}>
                              {renderActionButtons(ticket)}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* DataTables footer - Laravel exact match */}
                  <div className={`${styles.row}`}>
                    <div className={`${styles['col-sm-5']}`}>
                      <div className={`${styles.dataTables_info}`}>
                        Showing {startRecord} to {endRecord} of {totalEntries} entries
                        {clickedStatusId && (
                          <span> (filtered by status)</span>
                        )}
                      </div>
                    </div>
                    <div className={`${styles['col-sm-7']}`}>
                      <div className={`${styles.dataTables_paginate}`}>
                        {totalPages > 1 && (
                          <>
                            <button
                              className={`${styles.paginate_button} ${currentPage === 1 ? styles.disabled : ''}`}
                              onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
                              disabled={currentPage === 1}
                            >
                              Previous
                            </button>
                            {Array.from({ length: Math.min(10, totalPages) }, (_, i) => {
                              const page = i + 1;
                              if (totalPages > 10) {
                                if (page <= 3 || page > totalPages - 3 ||
                                    (page >= currentPage - 1 && page <= currentPage + 1)) {
                                  return (
                                    <button
                                      key={page}
                                      className={`${styles.paginate_button} ${currentPage === page ? styles.current : ''}`}
                                      onClick={() => setCurrentPage(page)}
                                    >
                                      {page}
                                    </button>
                                  );
                                }
                                return null;
                              }
                              return (
                                <button
                                  key={page}
                                  className={`${styles.paginate_button} ${currentPage === page ? styles.current : ''}`}
                                  onClick={() => setCurrentPage(page)}
                                >
                                  {page}
                                </button>
                              );
                            })}
                            <button
                              className={`${styles.paginate_button} ${currentPage === totalPages ? styles.disabled : ''}`}
                              onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
                              disabled={currentPage === totalPages}
                            >
                              Next
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                </div>
              ) : (
                <div>No tickets!</div>
              )}

            </div>
          </div>
        </div>

      </div>

      {/* ✅ FIX #6: Modal title "Ticket" instead of "Ticket from calendar" (LOW PRIORITY) */}
      {showTicketModal && selectedTicket && (
        <div 
          className={`${styles.modal} ${styles.fade} ${styles.in}`} 
          id="modal-ticket-from-calendar" 
          style={{ 
            zIndex: 1050
          }}
        >
          <div className={`${styles['modal-dialog']}`}>
            <div className={`${styles['modal-content']}`}>
              <div className={`${styles['modal-header']}`}>
                {/* Laravel exact match: close button with btn-modal-close class */}
                <button
                  type="button"
                  className={`${styles.close} btn-modal-close`}
                  onClick={() => setShowTicketModal(false)}
                >
                  <span>&times;</span>
                </button>
                {/* Laravel exact match: modal title "Ticket" */}
                <h4 className={`${styles['modal-title']}`}>Ticket</h4>
              </div>
              <div className={`${styles['modal-body']}`}>

                {/* Laravel ticket details table - exact structure from details.blade.php */}
                {/* NO input fields - Laravel shows only read-only fields */}
                <table className={`${styles['ticket-details-in-calendar-modal']}`}>
                  <tbody>
                    <tr>
                      <td>Title</td>
                      <td name="title">{selectedTicket.title}</td>
                    </tr>
                    <tr>
                      <td>Status</td>
                      <td name="ticket_status">{selectedTicket.ticket_status}</td>
                    </tr>
                    <tr>
                      <td>C. Contact</td>
                      <td name="contractor_user">{selectedTicket.contractor_user}</td>
                    </tr>
                    <tr>
                      <td>Contractor's decision</td>
                      <td name="contractor_decision">{selectedTicket.contractor_decision || ''}</td>
                    </tr>
                    <tr>
                      <td>Description</td>
                      <td name="calendar_description">{selectedTicket.calendar_description}</td>
                    </tr>
                  </tbody>
                </table>

                {/* Laravel action buttons - exact structure from modal_show.blade.php - buttons in modal body */}
                {/* NO Update button - Laravel doesn't have it */}
                {hasPermission('tickets.show') && (
                  <div className={`${styles['form-group']} ${styles['pull-left']} ${styles['inline-block']}`} style={{ marginTop: '20px' }}>
                    {/* Show button - blue/primary */}
                    <button
                      type="button"
                      className={`${styles.btn} ${styles.btnHead} ${styles.btnStandard} ${styles.btnPrimary}`}
                      onClick={() => navigate(`/tickets/${selectedTicket.ticketId}`)}
                    >
                      Show
                    </button>

                    {/* Accept and Decline buttons - only for REQUEST status (status_id === 1) */}
                    {hasPermission('tickets.contractors.manage') && (
                      <>
                        {selectedTicket.status_id === 1 && (
                          <>
                            {/* Accept button - green/success */}
                            <button
                              type="button"
                              className={`${styles.btn} ${styles.btnHead} ${styles.btnStandard} ${styles['bck-success']} ${styles['col-white']}`}
                              onClick={() => handleTicketAction(selectedTicket.ticketId, 'accept')}
                            >
                              Accept
                            </button>

                            {/* Decline button hidden for contractors - only staff can decline */}
                            {hasPermission('tickets.delete') && !isContractor() && (
                              <button
                                type="button"
                                className={`${styles.btn} ${styles.btnHead} ${styles.btnStandard} ${styles['bck-red']} ${styles['col-white']}`}
                                onClick={() => handleTicketAction(selectedTicket.ticketId, 'decline')}
                              >
                                Decline
                              </button>
                            )}
                          </>
                        )}

                        {/* Complete button - green (overrides warning color with #2e8b57) - for all statuses except COMPLETED (status_id !== 5) */}
                        {/* Laravel exact match: Complete button shows for all statuses except COMPLETED (status_id 5) */}
                        {/* Note: CONTRACTOR_COMPLETED (status_id 7) should still show Complete button */}
                        {/* Certificate renewal tickets: completed by uploading the certificate, not here. */}
                        {selectedTicket.status_id !== 5 && !selectedTicket.is_certificate_renewal && (
                          <button
                            type="button"
                            className={`${styles.btn} ${styles.btnHead} ${styles.btnStandard} ${styles['bck-warning']} ${styles['col-white']}`}
                            onClick={() => handleTicketAction(selectedTicket.ticketId, 'complete')}
                            style={{ backgroundColor: '#2e8b57' }}
                          >
                            Complete
                          </button>
                        )}
                      </>
                    )}

                    <div className={styles.clearfix}></div>
                  </div>
                )}

              </div>
              <div className={`${styles['modal-footer']}`}>
                {/* Laravel footer - only Close button */}
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnDefault} btn-modal-close`}
                  onClick={() => setShowTicketModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal backdrop */}
      {showTicketModal && (
        <div 
          className={`${styles['modal-backdrop']} ${styles.fade} ${styles.in}`} 
          style={{ 
            zIndex: 1040
          }}
        ></div>
      )}

    </div>
  );
};

export default ContractorContactCalendar;
